import React from 'react';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useWhoami } from '@cotera/client/app/stores/org';
import {
  CommentMarginHost,
  CommentsRailOutlet,
} from '@cotera/client/app/components/app/rte';
import { useIsArtifactFocusView } from '@cotera/client/v0/chat/artifact-panel-context';
import { useDocumentModel } from './document.model';
import { DocumentStateEditor } from './document-state-editor';
import { ResourceArtifactPanelProvider } from '../chat/artifacts/resource-artifact-panel';

/**
 * Document editor shell.
 *
 * The editor always scrolls inside a {@link CommentMarginHost}, which reserves a strip
 * for the comment cards wherever the pane is wide enough to spare one — the focus
 * view, mainly — and falls back to margin chips where it is not, as in the artifact
 * panel beside a chat.
 *
 * On the standalone page (`mode==='page'`) that sits beside a transient artifact side
 * panel, so clicking a resource mention opens it in place, with the comments rail
 * docked outside both as the last column. Embedded as an artifact in chat it renders
 * without the rail: the chat page owns that column there, so it sits beside the
 * artifact panel rather than inside it.
 */
export const DocumentEditLayout: React.FC<{
  skipNextBlockRef?: React.MutableRefObject<boolean>;
  disabled?: boolean;
}> = ({ disabled = false }) => {
  const model = useDocumentModel();
  const editorRef = model.editorRef;
  const whoami = useWhoami((x) => x.whoami);
  const editorInstance = useWatchableValue(model.editorInstanceWatchable);
  const isFocusView = useIsArtifactFocusView();

  const author = React.useMemo(
    () => ({ id: whoami.id, name: whoami.name ?? whoami.email }),
    [whoami.id, whoami.name, whoami.email]
  );

  const editor = (
    <CommentMarginHost editor={editorInstance} author={author}>
      <DocumentStateEditor
        className="min-h-full"
        editorRef={editorRef}
        editable={!disabled}
      />
    </CommentMarginHost>
  );

  if (model.mode === 'page') {
    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden h-full">
        <CommentsRailOutlet>
          <ResourceArtifactPanelProvider>
            {editor}
          </ResourceArtifactPanelProvider>
        </CommentsRailOutlet>
      </div>
    );
  }

  // As an artifact, the rail belongs here only in the focus view — under this
  // resource's own bar, so that bar spans the window. Beside a chat the split's own
  // outlet hosts it, one level further out. Mounted either way; see its `enabled`.
  return (
    <CommentsRailOutlet enabled={isFocusView}>{editor}</CommentsRailOutlet>
  );
};
